import * as angular from 'angular';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { AccountFacade } from '@mode/accounts/contract';
import { AvatarOptions } from '@mode/capra';
import { SpaceResource } from '@mode/legacy/data-access-model-js';
import { MenuOptionId, MenuOptionRecord } from '@mode/legacy/types-app-state';
import { ReportFacade, ViewDataModalSelectors } from '@mode/reports/domain';
import { DisplayMode, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { OrgTypes, UserTypes } from '@mode/shared/contract-common';
import { NewContainerMenuItem } from '@mode/shared/ui';
import { getCurrentUsername, getOwnerUsername } from '@mode/shared/util-js';
import { isPresent } from '@mode/shared/util-js';
import {
  CreateNewReportObservabilityService,
  ObservabilityEvents,
  ObservabilityService,
} from '@mode/shared/util-observability';
import { AnalyticsEvent } from '@mode/shared/util-segment';
import { MixableBaseController } from '~/common/component';
import { isExplorationRoute } from '~/lib/router-helpers';
import { FeatureToggleService } from '~/services/feature-toggle-service';
import { AppStore } from '~/types/main.types';
import { BugsnagHelpers } from '../../lib/bugsnag-helpers';
import { accountAvatarSelector, expandedViewOpenSelector } from '../../reducers/selectors';
import { PlatformHeaderSelectors } from '../../selectors/platform-header.selectors';
import DataSourceService from '../../services/data-source.service';
import { PlatformHeaderNavMenuType } from '../platform-header-nav/platform-header-nav-controller';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';

export enum PlatformHeaderStyle {
  Dark = 'Dark',
  Light = 'Light',
}

export enum Breakpoint {
  Small = 'Small',
  Large = 'Large',
}
export default class PlatformHeaderController extends MixableBaseController {
  // bindings
  menuType: PlatformHeaderNavMenuType;
  headerStyle: PlatformHeaderStyle;
  navPanelEnabled = true;
  standaloneEnabled = false;
  userName: string | undefined;

  // observables
  isLoggedIn$: Observable<boolean>;
  editAccess$: Observable<boolean>;
  expandedViewOpen$: Observable<boolean>;
  orgContextSubscription$: Subscription;
  starOptionSubscription$: Subscription;
  showUserAccessComponent$: Observable<boolean>;
  starOption: MenuOptionRecord<MenuOptionId.AllTypes>;
  orgSpaces$: Observable<SpaceResource[]>;
  orgContext$: Observable<OrgTypes.Organization>;
  account$: Observable<UserTypes.User>;
  accountContext$: Observable<OrgTypes.Organization>;
  intercomChatEnabled$: Observable<boolean>;
  showModeStudio$: Observable<boolean>;
  avatar: AvatarOptions;
  isAnalystStudio$: Observable<boolean>;

  // auxiliary app state references
  _orgContextNewReportLink: string;
  _orgContextNewReportLinkAvailable: boolean;
  _breakpoint: Breakpoint;
  _sources = {
    expandedViewOpen: new BehaviorSubject(null),
    orgSpaces: new BehaviorSubject(null),
  };
  viewDataModalOpen: boolean;
  newContainerMenuItems$: Observable<NewContainerMenuItem[]>;
  pageContext: string;

  // deps
  private DataSourceService: DataSourceService;

  // flags
  private queryMetricsEnabled$: Observable<boolean>;

  onSimpleShare?: () => void;

  static $inject = [
    '$injector',
    '$window',
    '$attrs',
    '$timeout',
    'RxStoreService',
    'ModeCollection',
    'AccountFacade',
    'AccountModel',
    'DataSourceModel',
    'SpaceModel',
    '$state',
    'FeatureToggleService',
    'CreateNewReportObservabilityService',
    'ObservabilityService',
    'ReportFacade',
    'FeatureFlagsFacade',
    'ThoughtspotIntegrationService',
  ];

  constructor(
    private $injector,
    private $window: angular.IWindowService,
    private $attrs,
    private $timeout,
    private store: AppStore,
    private ModeCollection,
    private accountFacade: AccountFacade,
    private AccountModel,
    private DataSourceModel,
    private SpaceModel,
    private $state,
    private featureToggleService: FeatureToggleService,
    private createNewReportObservabilityService: CreateNewReportObservabilityService,
    private observabilityService: ObservabilityService,
    private reportFacade: ReportFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {
    super();

    this.observabilityService.mark(ObservabilityEvents.HEADER_LOADING);
    this.isLoggedIn$ = accountFacade.isLoggedIn$;
    this.orgContext$ = accountFacade.currentOrg$;
    this.account$ = accountFacade.user$;
    this.accountContext$ = accountFacade.currentOrg$;
    this.intercomChatEnabled$ = featureToggleService.featureIntercomChatEnabled$;

    this.isAnalystStudio$ = this.thoughtspotIntegrationService.currOrgIntegrated$;
    this.showModeStudio$ = combineLatest([
      of(this.isExploreRoute()),
      featureToggleService.featureModeStudioEnabled$,
    ]).pipe(map(([isExploreRoute, modeStudioEnabled]) => !isExploreRoute && modeStudioEnabled));

    this.store
      .select(accountAvatarSelector)
      .pipe(filter(isPresent), takeUntil(this.destroyed$))
      .subscribe((avatar: any) => (this.avatar = avatar));

    if (this.$attrs.standaloneEnabled === 'true') {
      this.expandedViewOpen$ = this._sources.expandedViewOpen.asObservable();
      this.orgSpaces$ = this._sources.orgSpaces.asObservable();
      // load resources
      this.updateLocalState();
    } else {
      this.expandedViewOpen$ = this.store.select(expandedViewOpenSelector);
      this.orgSpaces$ = this.store
        .select(PlatformHeaderSelectors.SelectorFactories.orgSpaces(this.SpaceModel))
        .pipe(map((models) => models && models.sort(this.SpaceModel.navbarSort)));
      // conditionally load datasource service for nav panel
      if (this.navPanelEnabled && this.$injector.has('DataSourceService')) {
        this.DataSourceService = this.$injector.get('DataSourceService');
      }
    }

    this.editAccess$ = this.getEditAccess();
  }

  $onInit() {
    this._breakpoint = this.getCurrentBreakpoint();
    this.userName = getCurrentUsername(this.$window.document);

    this.orgContextSubscription$ = this.orgContext$.subscribe((orgCtxModel) => {
      // Note - we're building these links manually so this works in all instances of this behavior since the related
      //   routes aren't necessarily defined everywhere.
      let newReportLink = '';
      const newReportLink$ = new BehaviorSubject<string>('');
      const newDatasetLink = `/editor/${orgCtxModel.username}/datasets/new`;
      this._orgContextNewReportLink = orgCtxModel.links.newReport;

      this.queryMetricsEnabled$ = this.accountFacade.currentOrg$.pipe(
        map((currentOrg) => currentOrg?.features.dbtMetrics)
      );

      this.queryMetricsEnabled$.pipe(first()).subscribe((queryMetricsEnabled) => {
        newReportLink = `/editor/reports/new`;
        if (queryMetricsEnabled) {
          this._orgContextNewReportLink = newReportLink;
        }

        newReportLink$.next(newReportLink);
      });
      this.newContainerMenuItems$ = this.getMenuItems(newReportLink$, newDatasetLink);
      this._orgContextNewReportLinkAvailable = orgCtxModel.links.newReport != null;
    });

    this.subscribeToMediaQueryBreakpoints((value: Breakpoint) => {
      this._breakpoint = value;
    });

    this.store
      .select(ViewDataModalSelectors.selectIsOpen)
      .pipe(takeUntil(this.destroyed$))
      // Explore header needs to be enabled for view data modal TODO: Remove post GA Explore
      .subscribe(
        (isOpen) =>
          (this.viewDataModalOpen =
            isOpen &&
            // Disable explore header if new modal is in place on report view
            !this.$state.is('reportShow.noRuns.dashboard') &&
            // Disable explore header on Edit page
            !this.$state.is('application.reports.edit'))
      );

    if (this.userName) {
      this.accountFacade.loadCurrentUser(this.userName);
    }
  }

  $onDestroy() {
    this.orgContextSubscription$ && this.orgContextSubscription$.unsubscribe();
  }

  getEditAccess(): Observable<boolean> {
    return this.reportFacade.headerReportData$.pipe(map((reportData) => !!reportData?._forms?.['edit']));
  }

  showUserAccessAvatar(): boolean {
    return (
      this.navDisplayMode === DisplayMode.Menu &&
      this.menuType === 'Report' &&
      !this.showExploreHeader() &&
      !!this.avatar
    );
  }

  getMenuItems(newReportLink$: Observable<string>, newDatasetLink: string): Observable<NewContainerMenuItem[]> {
    return newReportLink$.pipe(
      map((reportLink) => {
        return [
          {
            href: reportLink,
            text: 'Report',
            icon: 'icon-report-builder',
            class: 'new-report',
            testAttribute: 'create-new-report',
            trackClickData: {
              location: this.pageContext,
              action: AnalyticsEvent.Name.NewReport,
            },
          },
          {
            href: newDatasetLink,
            text: 'Dataset',
            icon: 'icon-dataset',
            class: 'new-dataset',
            testAttribute: 'create-new-dataset',
            trackClickData: {
              location: this.pageContext,
              action: AnalyticsEvent.Name.NewDataset,
            },
          },
        ];
      })
    );
  }

  async updateLocalState(): Promise<void> {
    const accountId = this.userName;
    const accountContextId = getOwnerUsername(this.$window.document);

    if (!accountContextId) {
      BugsnagHelpers.notifySafely(new Error('unable to get account (organization) from meta tags'), (event) => {
        event.groupingHash = 'OrganizationMetaMissing';

        event.addMetadata('context', {
          username: accountId,
          account: accountContextId,
          html: this.$window.document.head.innerHTML,
        });
      });

      console.error('Error retrieving username information');
    } else {
      try {
        await this.loadResourcesToLocalState(accountContextId);
      } catch (err) {
        BugsnagHelpers.notifySafely(err);
      }
    }
  }

  async loadResourcesToLocalState(accountContextId: string) {
    const accountContext = await this.AccountModel.find({
      username: accountContextId,
      useEntryPath: true,
      includes: ['organization_features'],
    });

    const orgSpaces = accountContext._resource.hasLink('spaces')
      ? await this.ModeCollection.elements(await accountContext.spaces())
      : [];

    this.$timeout(() => {
      this._sources.expandedViewOpen.next(false); // disable for standalone mode
      this._sources.orgSpaces.next(orgSpaces.sort(this.SpaceModel.navbarSort));
    });
  }

  get orgContextNewReportLink(): string {
    return this._orgContextNewReportLink;
  }

  get orgContextNewReportLinkIsAvailable(): boolean {
    return this._orgContextNewReportLinkAvailable;
  }

  get navDisplayMode(): DisplayMode {
    if (this._breakpoint === Breakpoint.Large) {
      return DisplayMode.Menu;
    } else {
      return DisplayMode.Dropdown;
    }
  }

  getHeaderClasses() {
    return {
      'editor-top-nav': this.headerStyle === PlatformHeaderStyle.Dark,
      'header-style-type-dark': this.headerStyle === PlatformHeaderStyle.Dark,
      'header-nav-mode-dropdown': this.navDisplayMode === DisplayMode.Dropdown,
      'header-nav-mode-menu': this.navDisplayMode === DisplayMode.Menu,
    };
  }

  handleSimpleShare(): void {
    this.onSimpleShare && this.onSimpleShare();
  }

  isExploreRoute(): boolean {
    return isExplorationRoute(this.$state);
  }

  showExploreHeader(): boolean {
    return this.viewDataModalOpen || this.isExploreRoute();
  }

  subscribeToMediaQueryBreakpoints(cb) {
    this.$window.matchMedia('(max-width: 825px)').addListener((e) => {
      this.$timeout(() => {
        cb(e.matches ? Breakpoint.Small : Breakpoint.Large);
      });
    });
  }

  getCurrentBreakpoint() {
    return this.$window.document.body.clientWidth <= 768 ? Breakpoint.Small : Breakpoint.Large;
  }

  getStudioBadgeTheme() {
    switch (this.headerStyle) {
      case PlatformHeaderStyle.Dark:
        return 'dark';
      case PlatformHeaderStyle.Light:
        return 'light';
      default:
        return null;
    }
  }
}
